// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * A precondition a declared period carries, in platform vocabulary.
 *
 * Sprint 65.9.5. Some statutory implementation periods are not unconditional,
 * and the one that forced this is the Vietnamese withdraw/restrict/object
 * window: the declaring article states the period and then excepts processing
 * that needs no consent in the cases ONE named article of its parent law lists.
 * It does not except every processing on a ground other than consent. Before
 * this member the deadline schema had **no way to say so** — the record stated
 * the period flatly, and the platform raised the clock on every such request
 * as though it were unconditional. Raising the clock on every request is kept:
 * where the text supports two readings it is the reading that protects the data
 * subject (fix-sprint-20261003-0625 F3, recorded in the deadline notes). What was
 * wrong was presenting it without its condition; with this member the operator
 * sees a period that may not apply, not one silently presented as unconditional.
 *
 * Named for the CONDITION and not for the instrument, per R-46. The pack says
 * "this period may not apply where the processing falls in a case the declaring
 * article excepts"; it does not name an article here, and the platform does not
 * learn a jurisdiction's vocabulary from a field name. The citation lives in the
 * record's `reference` and `note`, where a citation belongs.
 *
 * - `processing_not_in_excepted_no_consent_case` — fix-sprint-20261003-0625 F3
 *   (the independent legal reading). The period runs on every request of its
 *   class EXCEPT one against processing the organisation records as falling in
 *   the no-consent case the declaring article, or the provision granting the
 *   right it serves, names (the record's `reference` and `note` cite it). The platform does not decide whether a processing on
 *   another consent-free ground is such a case, and it raises the clock there
 *   too. This is the narrower condition the text states; the member below
 *   encoded a wider one.
 * - `processing_required_consent` — LEGACY, kept so no stored record or clock
 *   snapshot breaks. Read with the meaning it always had: the period may not
 *   run where the processing the request targets needed no consent. Its name
 *   is wider than the exception it stood for, which is why the packs no longer
 *   author it; a deadline record installed before the pack moved, and every
 *   snapshot already stored, still carries it, and the resolver still accepts
 *   it. Both members are carried and never applied — the clock is raised either
 *   way — so the two readings never produce different deadlines.
 */
export const DEADLINE_PRECONDITIONS = [
    'processing_not_in_excepted_no_consent_case',
    'processing_required_consent',
];
/** The legacy member: accepted on stored records and snapshots, never authored. */
export const LEGACY_DEADLINE_PRECONDITIONS = [
    'processing_required_consent',
];
/**
 * Sprint 63.31.0 (#413, #422; owner rulings Q1 (a), Q2 (b), OD-A) — what dates
 * one clock of a rights request.
 *
 * - `statutory` — an installed clock in force at the request's receipt dates
 *   it (or, received before the clock came into force, since its activation
 *   date: decision E2). The snapshot names the record.
 * - `tenant_target` — no installed clock in force dates it, and the
 *   organisation's own saved SLA target for the type does (Settings › SLA).
 * - `platform_default` — neither: `PLATFORM_DEFAULT_RIGHTS_PERIODS`, shown as
 *   *platform default, not a legal period* (decision E6).
 * - `none` — nothing dates it. Only the acknowledgement clock can read `none`:
 *   no organisation target or platform default exists for it.
 */
export const RIGHTS_CLOCK_SOURCES = [
    'statutory',
    'tenant_target',
    'platform_default',
    'none',
];
/**
 * Sprint 63.31.0 — the dating state of a case (and of each clock).
 *
 * - `dated` — an installed clock in force dates it.
 * - `awaiting_activation` — the only installed clock for it is not yet in force
 *   (owner ruling Q1 (a)): the case is accepted, dated meanwhile from the
 *   organisation's target or the platform default, and dated by the statutory
 *   clock on its activation day by the daily rights-deadline sweep, an atomic
 *   `awaiting_activation → dated` transition (decision E3).
 * - `no_installed_clock` — no installed clock applies to the class (OD-A: the
 *   request is still accepted); the interim date is all there is.
 */
export const RIGHTS_DEADLINE_STATES = [
    'dated',
    'awaiting_activation',
    'no_installed_clock',
];
// Sprint 63.8.2 gate E12.5 remediation A — a value, so the route schema's
// `z.enum` is built from this list rather than a second hand-written copy.
export const QUALITY_GATE_OPERATORS = [
    'eq',
    'neq',
    'in',
    'not_in',
    'exists',
    'not_exists',
];
// Sprint 63.8.2 gate E12.5 remediation A — a value, so the route schema's
// `z.enum` is built from this list rather than a second hand-written copy.
export const CASE_COMMUNICATION_CHANNELS = [
    'email',
    'portal',
    'phone',
    'postal',
    'in_person',
];
// ── Sprint 4.0.0 -- Board Report ─────────────────────────────────
/**
 * The languages a board report can be generated in. The route's request schema
 * parses against this array and the Board Report page offers it, so the form
 * cannot offer a language the server refuses.
 */
export const BOARD_REPORT_LANGUAGES = ['en', 'vi'];
/**
 * Gate E12.5 remediation 1 (fix-sprint #431) — how long a call may hold a
 * dossier's filing in `opening` before another call may take it over: a call
 * that crashed while opening the filing must not leave it unretryable.
 * Remediation 2: one constant, read by the server's takeover and by the case
 * page, which offers the retry over an `opening` older than this.
 */
export const CROSS_BORDER_FILING_OPENING_STALE_MS = 5 * 60_000;
//# sourceMappingURL=case.js.map